// Adyvance Wallet in the shell (BILLING-001, D-064): the top-bar balance chip and the top-up popup
// that any screen can open (`useWalletTopup().open({ amountPaisa })`), e.g. a purchase that is
// short of money opens it with the shortfall filled in. The popup itself is code-split.
import {
  createContext,
  lazy,
  Suspense,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { WalletOverview } from "@ady/contracts";
import { formatDate, formatMoney } from "@ady/format";
import { t } from "@ady/i18n";
import { Button, cx, Icon, Popup } from "@ady/ui";
import { AppLink } from "../shell/AppLink";
import { useShell } from "../shell/context";
import { can } from "../shell/nav";
import { usePopover } from "../shell/usePopover";
import { useWallet } from "./api";
import { clearFonepayResult, fonepayResult } from "./payReturn";
import { useWalletOverview } from "./walletApi";

const TopupPopup = lazy(() => import("./TopupPopup").then((m) => ({ default: m.TopupPopup })));
const FonepayReturn = lazy(() => import("./FonepayQr").then((m) => ({ default: m.FonepayReturn })));

interface TopupRequest {
  /** Prefill (e.g. what a purchase is short of). */
  amountPaisa?: number;
  /** Called after the top-up was sent for checking. */
  onSent?: () => void;
}

const TopupCtx = createContext<{ open: (r?: TopupRequest) => void } | null>(null);

export function WalletTopupProvider({ children }: { children: ReactNode }) {
  const { membership } = useShell();
  const [req, setReq] = useState<TopupRequest | null>(null);
  // D-122: back from Fonepay's payment page (main.tsx kept the payment to show).
  const [returned, setReturned] = useState(fonepayResult);
  const value = useMemo(() => ({ open: (r: TopupRequest = {}) => setReq(r) }), []);
  return (
    <TopupCtx.Provider value={value}>
      {children}

      {req ? (
        <Suspense fallback={null}>
          <TopupPopup
            orgId={membership.org.id}
            amountPaisa={req.amountPaisa}
            onClose={() => setReq(null)}
            onSent={() => {
              req.onSent?.();
              setReq(null);
            }}
          />
        </Suspense>
      ) : null}
      {returned ? (
        <Suspense fallback={null}>
          <Popup
            open
            onClose={() => {
              clearFonepayResult();
              setReturned(null);
            }}
            label={t("adyWallet.online.webTitle")}
            size="sm"
          >
            <FonepayReturn
              orgId={membership.org.id}
              paymentId={returned}
              onClose={() => {
                clearFonepayResult();
                setReturned(null);
              }}
            />
          </Popup>
        </Suspense>
      ) : null}
    </TopupCtx.Provider>
  );
}

/** Opens the top-up popup; a no-op outside the shell. */
export function useWalletTopup(): { open: (r?: TopupRequest) => void } {
  return useContext(TopupCtx) ?? { open: () => {} };
}

/** Top bar: the available balance with a warning dot when low. A click opens the wallet summary
 * (owner, 2026-10-25): the balance and how long it lasts, this month, what is coming up, the plan
 * and credits, then Top up and Open wallet. */
export function WalletChip() {
  const { membership, orgSlug, storeSlug } = useShell();
  const allowed = can(membership, ["billing.read"]);
  const wallet = useWallet(membership.org.id, allowed);
  const topup = useWalletTopup();
  const { open, setOpen, ref } = usePopover();
  // The full summary loads only once the menu is opened; the chip keeps the small balance read.
  const overview = useWalletOverview(membership.org.id, allowed && open);
  if (!allowed) return null;
  const o = overview.data;
  const w = wallet.data?.wallet;
  const amount = w ? formatMoney(w.available_paisa) : "…";
  return (
    <div className="relative me-1 hidden md:block" ref={ref}>
      <button
        type="button"
        aria-label={t("adyWallet.chip", { amount })}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={t("adyWallet.title")}
        onClick={() => setOpen((v) => !v)}
        className={cx(
          "relative flex min-h-11 items-center gap-2 rounded-sm border border-card bg-surface px-3 text-sm font-semibold text-fg hover:border-link hover:bg-selected lg:min-h-control",
          open && "border-link bg-selected",
        )}
        data-testid="wallet-chip"
      >
        <Icon name="wallet" gradient />
        <span className="tabular-nums">{amount}</span>
        <Icon name="chevronDown" className="text-fg-tertiary" />
        {w?.low ? (
          <span
            className="absolute -end-0.5 -top-0.5 size-2.5 rounded-full border-2 border-surface bg-warning-fg"
            aria-hidden
          />
        ) : null}
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label={t("adyWallet.menu.label")}
          className="absolute end-0 top-full z-(--ady-z-popover) mt-2 flex w-64 flex-col rounded-md border border-subtle bg-overlay shadow-2"
          data-testid="wallet-menu"
        >
          {o ? (
            <WalletSummary
              o={o}
              base={`/${orgSlug}/${storeSlug}`}
              onClose={() => setOpen(false)}
              onTopup={() => {
                setOpen(false);
                topup.open();
              }}
            />
          ) : (
            <div className="h-32 animate-pulse rounded-md bg-sunken" />
          )}
        </div>
      ) : null}
    </div>
  );
}

const n = (v: number) => v.toLocaleString("en-IN");

/** One line of the summary: a label on the left, the value on the right. */
function Line({ label, children, title }: { label: string; children: ReactNode; title?: string }) {
  return (
    <span className="flex items-center justify-between gap-3 text-xs" title={title}>
      <span className="flex-none text-fg-secondary">{label}</span>
      <span className="min-w-0 truncate text-end font-medium tabular-nums">{children}</span>
    </span>
  );
}

function WalletSummary({
  o,
  base,
  onClose,
  onTopup,
}: {
  o: WalletOverview;
  base: string;
  onClose: () => void;
  onTopup: () => void;
}) {
  const w = o.wallet;
  const s = o.services;
  const runway = o.insights.runway_days;
  const next = o.insights.upcoming[0];
  const lasts =
    runway === null
      ? t("adyWallet.balance.noSpend")
      : runway > 365
        ? t("adyWallet.balance.lastsLong")
        : t("adyWallet.balance.lasts", { days: n(runway) });
  const plan =
    s.plan.kind === "paid" && s.plan.ends_at
      ? t("adyWallet.menu.planUntil", { plan: s.plan.name, date: formatDate(s.plan.ends_at) })
      : s.plan.name;
  return (
    <>
      <div className="flex flex-col gap-0.5 border-b border-subtle px-3 py-2">
        <span className="flex items-center justify-between gap-2 text-2xs text-fg-tertiary">
          {t("adyWallet.balance.available")}
          {w.low ? (
            <span className="rounded-full bg-warning-bg px-1.5 text-2xs font-semibold text-warning-fg">
              {t("adyWallet.balance.low")}
            </span>
          ) : null}
        </span>
        <span className="flex items-baseline justify-between gap-2">
          <span className="text-md font-semibold tabular-nums">
            {formatMoney(w.available_paisa)}
          </span>
          <span className="inline-flex items-center gap-1 text-2xs text-fg-tertiary" title={lasts}>
            <Icon name="timer" className="size-3" />
            {runway === null
              ? "—"
              : runway > 365
                ? t("adyWallet.menu.lastsLong")
                : t("adyWallet.menu.lasts", { days: n(runway) })}
          </span>
        </span>
        {w.reserved_paisa > 0 || o.pending_paisa > 0 ? (
          <span className="flex flex-wrap gap-x-2 text-2xs text-fg-tertiary">
            {w.reserved_paisa > 0 ? (
              <span>
                {t("adyWallet.balance.held")}: {formatMoney(w.reserved_paisa)}
              </span>
            ) : null}
            {o.pending_paisa > 0 ? (
              <span>
                {t("adyWallet.balance.waiting", { amount: formatMoney(o.pending_paisa) })}
              </span>
            ) : null}
          </span>
        ) : null}
      </div>
      <div className="flex flex-col gap-1 border-b border-subtle px-3 py-2">
        <Line label={t("adyWallet.menu.month")}>
          <span className="text-success-fg">+{formatMoney(o.insights.month.added_paisa)}</span>
          <span className="text-fg-tertiary"> · </span>−{formatMoney(o.insights.month.spent_paisa)}
        </Line>
        {next ? (
          <AppLink
            href={`${base}/${next.link}`}
            onClick={onClose}
            title={`${next.title}${next.due_at ? ` · ${t("adyWallet.upcoming.due", { date: formatDate(next.due_at) })}` : ""} · ${next.covered ? t("adyWallet.upcoming.covered") : t("adyWallet.upcoming.short")}`}
            className="-mx-1 flex items-center justify-between gap-3 rounded-xs px-1 text-xs text-fg no-underline hover:bg-hover"
          >
            <span className="min-w-0 truncate text-fg-secondary">
              {t("adyWallet.menu.next")}
              {next.due_at ? ` · ${formatDate(next.due_at)}` : ""}
            </span>
            <span className="inline-flex flex-none items-center gap-1 font-medium tabular-nums">
              <span
                className={cx(
                  "size-1.5 rounded-full",
                  next.covered ? "bg-success-fg" : "bg-warning-fg",
                )}
                aria-hidden
              />
              {formatMoney(next.amount_paisa)}
            </span>
          </AppLink>
        ) : null}
        <Line label={t("adyWallet.spend.plan")}>{plan}</Line>
        <Line label={t("adyWallet.menu.sms")}>
          {t("adyWallet.menu.smsLeft", { count: n(s.sms.credits) })}
        </Line>
        <Line label={t("adyWallet.menu.ai")}>
          {s.ai.month_limit
            ? t("adyWallet.menu.aiUsed", { used: n(s.ai.month_used), limit: n(s.ai.month_limit) })
            : t("adyWallet.menu.aiExtra", { extra: n(s.ai.extra_credits) })}
        </Line>
      </div>
      <div className="flex gap-1.5 p-2">
        <Button
          size="sm"
          icon="plus"
          className="flex-1"
          onClick={onTopup}
          data-testid="wallet-menu-topup"
        >
          {t("adyWallet.balance.topup")}
        </Button>
        <AppLink
          href={`${base}/wallet`}
          onClick={onClose}
          className="flex min-h-control flex-1 items-center justify-center rounded-xs border border-control px-3 text-sm font-medium text-fg no-underline hover:bg-hover"
          data-testid="wallet-menu-open"
        >
          {t("adyWallet.menu.open")}
        </AppLink>
      </div>
    </>
  );
}
